/**
 * @author NikolaV
 */

import { Ajax } from '@shared/Ajax';
import { FilesDownloadDialog } from '@shared/files/FilesDownloadDialog';
import { Notify } from '@shared/ui-native/Notify';
import { DateUtils } from '@shared/utils/DateUtils';
import { getFilenameWithVersion } from '@shared/utils/utils';
import { DateTime } from 'luxon';
import { WebviewInterface } from '@shared/WebviewInterface';


function fetchAll(): JQueryPromise<{
    images: IImage[]
}> {
    return Ajax({
        url: `ajax_images_with_creator.json`,
        data: {
            projectId: kuura.getProjectId()
        }
    }).then(resp => {
        resp.images.forEach(image => {
            if (image.originalDate === 0) { // usually its null, but bad data is accepted by server
                image.originalDate = null;
            }
        })
        return resp;
    })
}

function getImageById(id: number | string): JQueryPromise<IImage> {
    return Ajax({
        url: 'ajax_image.json',
        data: {
            id
        }
    }).then(resp => resp.ajaxImage)
}

function remove(imageIds: number[]): JQueryPromise<any> {

    return Ajax({
        url: 'images_bulk_delete.json',
        method: 'POST',
        data: {
            imageIds
        }
    });
}

function download(imageIds: number[], name: string): JQueryPromise<any> {
    if (!kuura.hasPrivilege('Download_Image')) {
        Notify.warn(__('general.validation.access_not_allowed'));
        return;
    }

    if (imageIds && imageIds.length == 1) {
        const fullPath = kuura.getContextPath() + `/api/1/file/${imageIds[0]}/get`;
        if (WebviewInterface.hasAndroidInterface()) {
            if (name && !utils.getExtensionFromFilename(name)) name += '.jpg';
            WebviewInterface.downloadDocument(imageIds[0], fullPath, name);
        } else {
            window.location.href = location.origin + fullPath;
            return $.Deferred().resolve();
        }
    } else {
        // Queue zipping images and show dialog
        const promise = getDownloadUuid(imageIds);
        promise.done(({ uuid }) => {
            FilesDownloadDialog.open({
                uuid
            })
        }).fail(err => {
            if (Array.isArray(err?.errorMessages)) {
                Notify.error(err?.errorMessages[0]);
            }
        });
        return promise;
    }
}

function downloadFolder(folderId: number): JQueryPromise<any> {
    if (!kuura.hasPrivilege('Download_Image')) {
        Notify.warn(__('general.validation.access_not_allowed'));
        return;
    }

    // Queue zipping images and show dialog
    const promise = getDownloadFolderUuid(folderId);
    promise.done(({ uuid }) => {
        FilesDownloadDialog.open({
            uuid
        })
    }).fail(err => {
        if (Array.isArray(err?.errorMessages)) {
            Notify.error(err?.errorMessages[0]);
        }
    });
    return promise;
}

function getDownloadUuid(imageIds: number[]): JQueryPromise<{
    uuid: string
}> {
    return Ajax({
        url: 'api/1/zip/queue_images',
        method: 'POST',
        data: {
            ids: imageIds
        }
    })
}

function getDownloadFolderUuid(folderId: number): JQueryPromise<{
    uuid: string
}> {
    return Ajax({
        url: 'api/1/zip/queue_images',
        method: 'POST',
        data: {
            folderId
        }
    });
}

/*
 * @param name - mobile apps use it for filename
 */
function openInNewTab(imageId: number, name: string): void {
    if (WebviewInterface.hasAndroidInterface()) {
        if (name && !utils.getExtensionFromFilename(name)) name += '.jpg';
        WebviewInterface.displayDocument(imageId, getFullImageUrl(imageId, 'o'), name);
    } else {
        window.open(getFullImageUrl(imageId, 'o'), '_blank');
    }
}

function getFullImageUrl(imageId: number, imageSize: 't' | 's' | 'm' | 'l' | 'o' | '4k' | '2k'): string {
    return kuura.getContextPath() + '/api/1/image/' + imageId + '_' + imageSize + '.jpg';
}

function renameImage(
    preset: string,
    originalName: string,
    folderName: string,
    description: string,
    originalDateM: Date,
    latLon: [number, number],
    activeModelName: string,
    bValue: string,
    alignment: { alignmentName: string, station: number },
    username: string
): string {
    if (!preset) return originalName;
    const { alignmentName, station } = alignment;
    const timezoneoffetH = -(new Date().getTimezoneOffset() / 60)

    let final = originalName;
    try {
        final = preset.replace('{alignmentname}', alignmentName ?? '?').
            replace('{station}', station != null ? String(station.toFixed(2)) : '?').
            replace('{ext}', utils.getExtensionFromFilename(originalName) ?? '?').
            replace('{organizationname}', kuura.getProjectOrganization().name ?? '?').
            replace('{projectname}', kuura.getProjectName() ?? '?').
            replace('{foldername}', folderName ?? '?').
            replace('{username}', username ?? kuura.getUsername() ?? '?').
            replace('{tz}', DateTime.local().toFormat('ZZZZ').split(/[+-]/)[0] ?? '?').
            replace('{tz-offset}', String(timezoneoffetH >= 0 ? '+' + timezoneoffetH : timezoneoffetH).replace(':', '')).
            replace('{originaldate}', originalDateM ? originalDateM.toISOString().split('T')[0] : '?' /* 2022-11-29*/).
            replace('{originaldatetz}', originalDateM ? (DateTime.fromJSDate(originalDateM).toFormat('ZZZZ').split(/[+-]/)[0] ?? '?') : '?').
            replace('{originaltime}', originalDateM ? originalDateM.toISOString().split('T')[1].split('.')[0] : '?' /* 11:10:36*/).
            replace('{simplename}', originalName.split('.').slice(0, -1).join('.')).
            replace('{photoname}', originalName).
            replace('{currentdate}', (new Date()).toISOString().split('T')[0] /* 2022-11-29*/).
            replace('{currenttime}', (new Date()).toISOString().split('T')[1].split('.')[0] /* 11:10:36*/).
            replace('{location}', latLon && latLon[0] ? latLon[0] + '_' + latLon[1] : '?').
            replace('{modelname}', activeModelName ?? '?').
            replace('{bvalue}', bValue ?? '?').
            replace('{description}', description ?? '?');
    } catch (e) {
        console.log('error while renaming photo:', e);
    }
    return final;
}

function getInfoTooltip(item: IImage | IDocument): string {
    const uploadDateMessage = __('map.image_upload_date_js');
    const origDateMessage = __('map.image_date_js');
    const creatorMessage = __('map.image_creator_js');
    const alignmentMessage = __('js_files.attached_alignment');
    const stationMessage = __('tablet.station_js');

    return (item.name ? getFilenameWithVersion(item) : '') +
        (item.description ? '\n' + item.description : '') +
        (item.originalDate ? '\n' + origDateMessage + ': ' + DateUtils.formatDateTime(item.originalDate) : '') +
        (item.timestamp ? '\n' + uploadDateMessage + ': ' + DateUtils.formatDateTime(item.timestamp) : '') +
        (item.creator ? '\n' + creatorMessage + ': ' + item.creator.username : '') +
        (item.alignmentName ? '\n' + alignmentMessage + ': ' + item.alignmentName : '') +
        (item.station >= 0 ? '\n' + stationMessage + ': ' + item.station : '')
}

export const ImagesService = {
    fetchAll,
    getImageById,
    remove,
    download,
    downloadFolder,
    openInNewTab,
    getFullImageUrl,
    renameImage,
    getInfoTooltip,
    defaultNameTemplate: '{originaldate}T{originaltime}{originaldatetz}.{ext}',
    nameTemplatesStorageKey: 'PHOTO_NAME_TEMPLATE'
};
